import React, { createRef, RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { mapModifiers } from 'libs/component';
import { FloorSelectorFullItem } from 'components/atoms/floor-selector-full-item';

import _ from 'lodash';
import { AnimateSharedLayout, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

interface FloorItemInfoProps {
  level: string;
  action?: boolean;
}

export interface FloorSelectorFullProps {
  floors: FloorItemInfoProps[];
  current?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onFloorActionClick?: (floor: string) => void;
}

const pcFloorMargin = -200;
const tlFloorMargin = -180;
const spFloorMargin = -100;
const spBreakpoint = 640;
const tlBreakpoint = 993;

export const FloorSelectorFull: React.FC<FloorSelectorFullProps> = ({
  floors,
  current: initialCurrent = 0,
  onFloorActionClick,
}) => {
  const { t } = useTranslation();
  const rootRef = useRef(null);
  const floorRefs = useRef([]);
  const [scrollPos, setScrollPos] = useState<number>(0);
  const [current, setCurrent] = useState(initialCurrent);
  const [currentIndex, setCurrentIndex] = useState<number>(-1);
  const [shouldScroll, setShouldScroll] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [isTouchDevice, setIsTouchDevice] = useState(false);

  if (floorRefs.current.length !== floors.length) {
    // add or remove refs
    floorRefs.current = new Array(floors.length).fill(undefined).map((_, i) => floorRefs.current[i] || createRef());
  }

  useEffect(() => {
    setCurrent(initialCurrent);
  }, [initialCurrent]);

  const handleFloorActionClick = (level: string) => {
    if (onFloorActionClick) {
      onFloorActionClick(level);
    }
  };

  const handleMouseOver = useCallback(
    (level: string, levelIndex: number) => {
      // check if this is mobile or not
      // const width = window.innerWidth || 0;
      // if (width > breakpoint) {
      if (!isTouchDevice) {
        setCurrent(level);
        setCurrentIndex(levelIndex);
      }
    },
    [isTouchDevice]
  );

  const handleMouseOut = useCallback(() => {
    // const width = window.innerWidth || 0;
    // if (width > breakpoint) {
    if (!isTouchDevice) {
      setCurrent('');
      setCurrentIndex(-1);
    }
  }, [isTouchDevice]);

  const handleClick = useCallback(
    (level, levelIndex) => {
      setCurrent(level);
      setCurrentIndex(levelIndex);
      const activeIndex = levelIndex;
      if (activeIndex !== -1) {
        const displayIndex = floors.length - activeIndex - 1;
        // get current element ref
        const elRef: RefObject<HTMLElement> = floorRefs?.current[displayIndex];
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const el: HTMLElement = elRef.current as any;
        if (el) {
          setShouldScroll(false);
        }
      }
    },
    [floors]
  );

  const handleResize = useCallback(() => {
    const width = window.innerWidth || 0;
    setIsMobile(width <= spBreakpoint);
    setIsTablet(width > spBreakpoint && width <= tlBreakpoint);
  }, []);

  useEffect(() => {
    if (window) {
      window.addEventListener('resize', handleResize);
    }
    handleResize();
    return () => {
      if (window) {
        window.removeEventListener('resize', handleResize);
      }
    };
  }, [handleResize]);

  useEffect(() => {
    const onScroll = _.debounce(() => {
      if ((isMobile || isTablet || isTouchDevice) && shouldScroll) {
        const newScrollTop = window.scrollY;
        // determine scroll direction
        const direction = scrollPos - newScrollTop > 0 ? 1 : -1;
        setScrollPos(newScrollTop);

        const bodyRect = document.body.getBoundingClientRect();

        // check if scroll top is hitting top position of which level
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let activeEl = -1;

        floorRefs?.current.forEach((elRef: RefObject<HTMLElement>, index) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const el: HTMLElement = elRef.current as any;
          const rect = el.getBoundingClientRect();
          const elTop = rect.top - bodyRect.top - 60;
          const elBottom = rect.bottom - bodyRect.top - 60;
          // now we check whether this element is passed a certain threshold position of the screen or not
          const hitPos = newScrollTop + (window.innerHeight - (window.innerHeight - rect.height) / 2);
          if (direction > 0) {
            // going up
            if (hitPos >= elBottom) {
              activeEl = index;
            }
          } else {
            // going down
            if (hitPos >= elTop) {
              activeEl = index;
            }
          }
        });
        // console.log('activeEl', activeEl);
        if (activeEl > -1) {
          const floorIndex = floors.length - activeEl - 1;
          setCurrent(floors[floorIndex].level);
          setCurrentIndex(activeEl);
          // handleMouseOver
        } else {
          setCurrent('');
          setCurrentIndex(-1);
        }
      }
    }, 33);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, [floors, scrollPos, shouldScroll, isMobile, isTouchDevice, isTablet]);

  useEffect(() => {
    if ('ontouchstart' in document.documentElement) {
      setIsTouchDevice(true);
    } else {
      setIsTouchDevice(false);
    }
  }, []);

  const getFloorYPosition = useCallback(
    floorIndex => {
      let margin = pcFloorMargin;
      if (isMobile) {
        margin = spFloorMargin;
      }
      if (isTablet) {
        margin = tlFloorMargin;
      }
      if (currentIndex !== -1 && currentIndex === floorIndex) {
        if (currentIndex === 0) return 0;
        // active floor - it's me!
        // console.log('active floor', floorIndex, margin * (floorIndex - 1));
        return margin * (floorIndex - 1);
      } else {
        if (currentIndex !== -1) {
          // inactive - some floor is active
          if (floorIndex < currentIndex) {
            // we should have space bottom
            // console.log('inactive [some floor]', floorIndex, margin * floorIndex);
            return margin * floorIndex;
          } else {
            // we should have space top & bottom
            if (currentIndex === 0) {
              // console.log('inactive [first floor]', floorIndex, margin * (floorIndex - 1));
              return margin * (floorIndex - 1);
            }
            // console.log('inactive [need top & bottom space]', floorIndex, margin * (floorIndex - 2));
            return margin * (floorIndex - 2);
          }
        } else {
          // inactive - no active floor
          // console.log('inactive [no floor]', floorIndex, margin * floorIndex);
          return margin * floorIndex;
        }
      }
    },
    [currentIndex, isMobile, isTablet]
  );

  return (
    <div
      className={mapModifiers('m-floor-selector-full', currentIndex !== -1 && 'active', isTouchDevice && 'touch')}
      ref={rootRef}
    >
      <AnimateSharedLayout type="crossfade">
        {floors
          .map(x => x)
          .reverse()
          .map((floor: FloorItemInfoProps, index) => (
            <motion.div
              key={index}
              ref={floorRefs.current[index]}
              initial={{ y: index === 0 ? 0 : isMobile ? -200 : isTablet ? -160 : -160 }}
              animate={{
                y: getFloorYPosition(index),
              }}
              transition={{
                delay: 0.25,
                duration: 0.5,
              }}
              className={mapModifiers('m-floor-selector-full__floor', current === floor.level ? 'active' : undefined)}
              onClick={() => handleClick(floor.level, index)}
              onMouseOver={() => handleMouseOver(floor.level, index)}
              onMouseOut={() => handleMouseOut()}
              style={{
                zIndex: floors.length - index,
              }}
            >
              <FloorSelectorFullItem
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                floor={floor.level as any}
                active={current === floor.level}
                action={floor.action ? t('venue.explore') : undefined}
                onActionClick={() => handleFloorActionClick(floor.level)}
              />
            </motion.div>
          ))}
      </AnimateSharedLayout>
    </div>
  );
};
